/**
 * evaluationProgress.ts — how much of an imported document has been evaluated
 * (carries a saved note code), for the Assessment summary on the details tab.
 *
 * The total is what normal mode renders: hidden table columns are removed and
 * hidden blocks dropped, exactly as StaticDocumentReview does it, so parts the
 * reviewer marked as irrelevant don't drag the percentage down.
 */

import { HastNode, parse } from "./hast";
import { collectBlockKeys } from "./segmenter";
import { applyTableVisibility, blockId, HiddenIndexMap } from "./tableVisibility";
import { NoteRecord } from "./annotationAdapter";

export interface ElementSource {
    id: number;
    contentText?: string | null;
}

export interface HiddenPartsSource {
    columns: HiddenIndexMap;
    blockIds: Set<string>;
}

/** Every annotatable block key still visible once hidden parts are removed. */
export function evaluableBlockKeys(elements: ElementSource[], hiddenParts: HiddenPartsSource): string[] {
    const nodes: HastNode[] = [];
    const elementIds: string[] = [];
    for (const el of elements) {
        for (const n of parse(el.contentText ?? "")) {
            nodes.push(n);
            elementIds.push(String(el.id));
        }
    }

    const visible = applyTableVisibility(nodes, elementIds, hiddenParts, "remove");

    // Block ids are numbered per element over ALL its nodes, the way segment()
    // does it, so the ids line up with the ones the reviewer hid.
    const ordinals = new Map<string, number>();
    const kept = visible.filter((_, i) => {
        const elementId = elementIds[i];
        const ordinal = ordinals.get(elementId) ?? 0;
        ordinals.set(elementId, ordinal + 1);
        return !hiddenParts.blockIds.has(blockId(elementId, ordinal));
    });

    return collectBlockKeys(kept);
}

/** Of `keys`, how many carry a saved note code. */
export function evaluationProgress(keys: string[], notes: NoteRecord[]): { evaluated: number; total: number } {
    const rated = new Set<string>();
    for (const nh of notes) {
        if (nh.currentNote?.note) rated.add(String(nh.blockKey));
    }
    return { evaluated: keys.filter((k) => rated.has(k)).length, total: keys.length };
}
